import React, { useState, useEffect } from "react";
import {
  Box, Typography, Paper, Button, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Grid, InputAdornment, Alert
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import SearchIcon from "@mui/icons-material/Search";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import * as XLSX from "xlsx";
import { db } from "../firebase";
import {
  collection, onSnapshot, addDoc, updateDoc,
  doc, orderBy, query, getDocs
} from "firebase/firestore";
import { salvaLog } from "../utils/logger";
import { useAuth } from "../context/AuthContext";

const CAMPI_VUOTI = {
  nome: "", cognome: "", email: "",
  telefono: "", azienda: "", ruolo: "", dipartimento: "",
};

export default function Referenti() {
  const { currentUser } = useAuth();
  const [referenti, setReferenti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(CAMPI_VUOTI);
  const [saving, setSaving] = useState(false);
  const [ricerca, setRicerca] = useState("");
  const [importando, setImportando] = useState(false);
  const [risultatoImport, setRisultatoImport] = useState(null);

  useEffect(() => {
    const q = query(collection(db, "referenti"), orderBy("cognome"));
    const unsub = onSnapshot(q, (snap) => {
      setReferenti(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    return unsub;
  }, []);

  function apriNuovo() {
    setEditId(null);
    setForm(CAMPI_VUOTI);
    setDialogOpen(true);
  }

  function apriModifica(referente) {
    setEditId(referente.id);
    setForm({ ...CAMPI_VUOTI, ...referente });
    setDialogOpen(true);
  }

  function handleChange(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  async function handleSalva() {
    if (!form.email.trim()) return;
    setSaving(true);
    try {
      if (editId) {
        const vecchio = referenti.find((r) => r.id === editId);
        await updateDoc(doc(db, "referenti", editId), form);
        const modifiche = [];
        const campiLabel = {
          nome: "Nome", cognome: "Cognome", email: "Email",
          telefono: "Telefono", azienda: "Azienda",
          ruolo: "Ruolo", dipartimento: "Dipartimento",
        };
        for (const [campo, label] of Object.entries(campiLabel)) {
          if (vecchio?.[campo] !== form[campo]) {
            modifiche.push({ campo: label, da: vecchio?.[campo] || "—", a: form[campo] || "—" });
          }
        }
        salvaLog(currentUser, "Modifica referente", "", `${form.nome} ${form.cognome}`, modifiche);
      } else {
        await addDoc(collection(db, "referenti"), form);
        salvaLog(currentUser, "Nuovo referente", "", `${form.nome} ${form.cognome}`, [
          { campo: "Email", da: "—", a: form.email }
        ]);
      }
      setDialogOpen(false);
    } catch (error) {
      console.error("Errore salvataggio referente:", error);
    }
    setSaving(false);
  }

  async function handleImport(e) {
    const file = e.target.files[0];
    if (!file) return;
    setImportando(true);
    setRisultatoImport(null);

    try {
      const buffer = await file.arrayBuffer();
      const wb = XLSX.read(buffer, { type: "array" });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const righe = XLSX.utils.sheet_to_json(ws, { header: 1 });

      const esistentiSnap = await getDocs(collection(db, "referenti"));
      const emailEsistenti = new Set(
        esistentiSnap.docs.map((d) => d.data().email?.toLowerCase()).filter(Boolean)
      );

      let importati = 0;
      let saltati = 0;

      const primaRiga = righe[0];
      const startIdx = primaRiga && typeof primaRiga[0] === "string" &&
        primaRiga[0].toLowerCase().includes("nom") ? 1 : 0;

      for (let i = startIdx; i < righe.length; i++) {
        const riga = righe[i];
        if (!riga || riga.length < 3) { saltati++; continue; }

        const nome = String(riga[0] || "").trim();
        const cognome = String(riga[1] || "").trim();
        const email = String(riga[2] || "").trim().toLowerCase();
        const azienda = String(riga[3] || "").trim();

        if (!email) { saltati++; continue; }
        if (emailEsistenti.has(email)) { saltati++; continue; }

        await addDoc(collection(db, "referenti"), {
          nome, cognome, email, azienda,
          telefono: "", ruolo: "", dipartimento: ""
        });
        emailEsistenti.add(email);
        importati++;
      }

      salvaLog(currentUser, "Import referenti", "", "", [
        { campo: "Importati", da: "—", a: String(importati) },
        { campo: "Saltati", da: "—", a: String(saltati) },
      ]);
      setRisultatoImport({ importati, saltati });
    } catch (error) {
      console.error("Errore import:", error);
      setRisultatoImport({ errore: error.message });
    }

    setImportando(false);
    e.target.value = "";
  }

  const referentiFiltrati = referenti.filter((r) => {
    const s = ricerca.toLowerCase();
    return (
      r.nome?.toLowerCase().includes(s) ||
      r.cognome?.toLowerCase().includes(s) ||
      r.email?.toLowerCase().includes(s) ||
      r.azienda?.toLowerCase().includes(s) ||
      r.ruolo?.toLowerCase().includes(s) ||
      r.dipartimento?.toLowerCase().includes(s)
    );
  });

  if (loading) return <Box display="flex" justifyContent="center" mt={8}><CircularProgress /></Box>;

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" fontWeight="bold">Referenti cliente</Typography>
        <Box display="flex" gap={1}>
          <Button variant="outlined" component="label"
            startIcon={importando ? <CircularProgress size={18} /> : <UploadFileIcon />}
            disabled={importando}>
            {importando ? "Importazione..." : "Importa Excel"}
            <input type="file" accept=".xlsx,.xls" hidden onChange={handleImport} />
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={apriNuovo}>
            Nuovo referente
          </Button>
        </Box>
      </Box>

      {risultatoImport && !risultatoImport.errore && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setRisultatoImport(null)}>
          Import completato — {risultatoImport.importati} referenti importati,{" "}
          {risultatoImport.saltati} saltati
        </Alert>
      )}
      {risultatoImport?.errore && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setRisultatoImport(null)}>
          Errore: {risultatoImport.errore}
        </Alert>
      )}

      <TextField fullWidth placeholder="Cerca per nome, cognome, email, azienda, ruolo o dipartimento..."
        value={ricerca} onChange={(e) => setRicerca(e.target.value)}
        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
        sx={{ mb: 3 }} />

      <TableContainer component={Paper} elevation={1}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: "#f0f4ff" }}>
              <TableCell><strong>Cognome</strong></TableCell>
              <TableCell><strong>Nome</strong></TableCell>
              <TableCell><strong>Email</strong></TableCell>
              <TableCell><strong>Telefono</strong></TableCell>
              <TableCell><strong>Azienda</strong></TableCell>
              <TableCell><strong>Ruolo</strong></TableCell>
              <TableCell><strong>Dipartimento</strong></TableCell>
              <TableCell align="center"><strong>Modifica</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {referentiFiltrati.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4, color: "text.secondary" }}>
                  Nessun referente trovato
                </TableCell>
              </TableRow>
            ) : (
              referentiFiltrati.map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell>{r.cognome || "—"}</TableCell>
                  <TableCell>{r.nome || "—"}</TableCell>
                  <TableCell>{r.email || "—"}</TableCell>
                  <TableCell>{r.telefono || "—"}</TableCell>
                  <TableCell>{r.azienda || "—"}</TableCell>
                  <TableCell>{r.ruolo || "—"}</TableCell>
                  <TableCell>{r.dipartimento || "—"}</TableCell>
                  <TableCell align="center">
                    <IconButton size="small" onClick={() => apriModifica(r)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editId ? "Modifica referente" : "Nuovo referente"}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} mt={0}>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Nome" value={form.nome}
                onChange={(e) => handleChange("nome", e.target.value)} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Cognome" value={form.cognome}
                onChange={(e) => handleChange("cognome", e.target.value)} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Email" type="email" value={form.email}
                onChange={(e) => handleChange("email", e.target.value)} required />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Telefono" value={form.telefono || ""}
                onChange={(e) => handleChange("telefono", e.target.value)} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Azienda" value={form.azienda}
                onChange={(e) => handleChange("azienda", e.target.value)} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Ruolo" value={form.ruolo || ""}
                onChange={(e) => handleChange("ruolo", e.target.value)} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Dipartimento" value={form.dipartimento || ""}
                onChange={(e) => handleChange("dipartimento", e.target.value)} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Annulla</Button>
          <Button variant="contained" onClick={handleSalva}
            disabled={saving || !form.email.trim()}>
            {saving ? <CircularProgress size={20} /> : "Salva"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}